import { SoggettoDto } from "@api/client";
import { companyDetailsSelector } from "common/_redux/company/selectors";
import { praticaRiprendiSelector } from "common/_redux/pratica/selectors";
import { subjectUpdateSubject } from "common/_redux/subject/actions";
import AppDatePicker from "common/components/app-date-picker";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import BftCodeInput from "common/components/code-input";
import CountriesDropdown from "common/components/countries-dropdown";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import BftRadioGroup from "common/components/radio-button";
import BftSelect from "common/components/select";
import { Formik, anagraficaSchema } from "common/validators/validators";
import { COUNTRIES, COUNTRIES_PREFIX } from "constants/countries";
import { BooleanValues, TipoSoggetto } from "constants/global";
import { genereOptions } from "constants/options";
import { authUserSelector } from "core/auth/_redux/selectors";
import { commonGetComuni } from "core/store/actions";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { commonComuniSelector, commonProvinceSelector } from "core/store/selectors";
import { invitationInvitationSelector } from "pages/invitation/_redux/selectors";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DropdownProps } from "semantic-ui-react";

const { TRUE, FALSE } = BooleanValues;

interface Props {
	subjectDetails?: SoggettoDto;
	idPratica?: string | undefined;
	idCliente?: string | undefined;
	onComplete: (email?: string) => void;
	skipSaveStepStatus?: boolean;
}

const Anagrafica: React.FC<Props> = (props) => {
	const { subjectDetails, onComplete, skipSaveStepStatus = false } = props;
	const dispatch = useAppDispatch();
	const user = useAppSelector(authUserSelector);
	const companyDetails = useAppSelector(companyDetailsSelector);

	const reopenPratica = useAppSelector(praticaRiprendiSelector);

	const province = useAppSelector(commonProvinceSelector);
	const comuni = useAppSelector(commonComuniSelector);
	const invitation = useAppSelector(invitationInvitationSelector);

	const [isInvitation, setIsInvitation] = useState(false);

	const codiceFiscaleAzienda =
		companyDetails?.infoAzienda?.codiceFiscale?.length === 16 &&
		companyDetails?.infoAzienda?.codiceFiscale;

	const handleProvinciaChange = (provincia: string | null | undefined) => {
		if (provincia) dispatch(commonGetComuni(provincia));
	};

	const _handleSubmit = (values: SoggettoDto) => {
		let subject: SoggettoDto = {
			...subjectDetails,
			...values,
		};

		dispatch(subjectUpdateSubject({ subject, skipSaveStepStatus }))
			.unwrap()
			.then((res) => {
				if (res) {
					onComplete(subject.email);
				}
			});
	};

	useEffect(() => {
		if (subjectDetails && TipoSoggetto.TE && invitation?.idToken) {
			setIsInvitation(true);
		} else {
			setIsInvitation(false);
		}
	}, [subjectDetails, invitation]);

	useEffect(() => {
		if (subjectDetails) {
			handleProvinciaChange(subjectDetails?.siglaProvinciaDiNascita);

			if (subjectDetails?.siglaProvinciaDiNascita !== subjectDetails?.siglaProvinciaResidenza) {
				handleProvinciaChange(subjectDetails?.siglaProvinciaResidenza);
			}
		}
	}, [subjectDetails]);

	return (
		<Formik
			initialValues={{
				nome:
					subjectDetails?.nome ||
					(subjectDetails?.tipoSoggetto === "Titolare" && "") ||
					(subjectDetails?.tipoSoggetto === "RappresentanteLegale"
						? user?.firstName || reopenPratica?.userData?.firstName
						: "") ||
					"",
				cognome:
					subjectDetails?.cognome ||
					user?.lastName ||
					(subjectDetails?.tipoSoggetto === "RappresentanteLegale"
						? user?.lastName || reopenPratica?.userData?.lastName
						: "") ||
					"",
				codiceFiscale: subjectDetails?.codiceFiscale || codiceFiscaleAzienda || "",
				genere: subjectDetails?.genere || "",
				conto: subjectDetails?.conto || false,
				dataDiNascita: subjectDetails?.dataDiNascita || undefined,
				statoDiNascita: subjectDetails?.statoDiNascita || "IT",
				siglaProvinciaDiNascita: subjectDetails?.siglaProvinciaDiNascita || "",
				comuneDiNascita: subjectDetails?.comuneDiNascita || "",
				numeroTel:
					subjectDetails?.numeroTel ||
					(subjectDetails?.tipoSoggetto === "Titolare" && "") ||
					(subjectDetails?.tipoSoggetto === "RappresentanteLegale"
						? user?.phoneNumber || reopenPratica?.userData?.phoneNumber
						: "") ||
					"",
				prefissoTel:
					subjectDetails?.prefissoTel ||
					(subjectDetails?.tipoSoggetto === "Titolare" && "") ||
					(subjectDetails?.tipoSoggetto === "RappresentanteLegale" ? user?.prefix : "") ||
					"+39",
				email:
					subjectDetails?.email ||
					(subjectDetails?.tipoSoggetto === "Titolare" ? "" : user?.email) ||
					(subjectDetails?.tipoSoggetto === "RappresentanteLegale"
						? user?.email || reopenPratica?.userData?.email
						: "") ||
					"",
				capResidenza: subjectDetails?.capResidenza || "",
				nazioneResidenza: subjectDetails?.nazioneResidenza || "IT",
				siglaProvinciaResidenza: subjectDetails?.siglaProvinciaResidenza || "",
				comuneResidenza: subjectDetails?.comuneResidenza || "",
				indirizzoResidenza: subjectDetails?.indirizzoResidenza || "",
				civicoResidenza: subjectDetails?.civicoResidenza || "",
				tipoSoggetto: subjectDetails?.tipoSoggetto,
				isInvitation: isInvitation,
			}}
			validationSchema={anagraficaSchema}
			onSubmit={_handleSubmit}
			enableReinitialize
		>
			{({
				handleSubmit,
				handleChange,
				setFieldValue,
				submitForm,
				validateForm,
				values,
				touched,
				errors,
			}) => {
				const nonoCarattereCF = values?.codiceFiscale?.charAt(9);
				const decimoCarattereCF = values?.codiceFiscale?.charAt(10);
				const controlloCFGenere =
					nonoCarattereCF && decimoCarattereCF && nonoCarattereCF + decimoCarattereCF;

				if (controlloCFGenere) {
					if (controlloCFGenere >= "0" && controlloCFGenere <= "31") {
						values.genere = "M";
					} else {
						values.genere = "F";
					}
				}

				const handleChangeCustom =
					(input: string, type?: string) => (e: any, d?: DropdownProps) => {
						if (type === "date") {
							setFieldValue(input, e);
							return;
						}
						if (type === "nations") {
							setFieldValue(input, (d?.value as string)?.toUpperCase());
							return;
						}
						if (type === "province") {
							let value = e.target.value;
							setFieldValue(input, value);
							handleProvinciaChange(value);

							if (input == "siglaProvinciaDiNascita")
								setFieldValue(
									"provinciaDiNascita",
									province?.find((p) => p.siglaProvincia == value)?.provincia
								);

							if (input == "siglaProvinciaResidenza")
								setFieldValue(
									"provinciaResidenza",
									province?.find((p) => p.siglaProvincia == value)?.provincia
								);

							return;
						}
						handleChange(e);
						setFieldValue(input, e === TRUE);
					};

				return (
					<form noValidate onSubmit={handleSubmit}>
						<Row className="d-flex align-items-center">
							<Col md={5}>
								<BftInput
									required
									type={"text"}
									name={"nome"}
									placeholder={"Nome*"}
									value={values.nome?.toUpperCase()}
									onChange={(e) => {
										const { name, value } = e.target;
										setFieldValue(name, value.toUpperCase());
									}}
									isInvalid={!!errors.nome && touched.nome}
								/>
							</Col>
							<Col md={6}>
								<BftInput
									required
									type={"text"}
									name={"cognome"}
									placeholder={"Cognome*"}
									value={values.cognome?.toUpperCase()}
									onChange={(e) => {
										const { name, value } = e.target;
										setFieldValue(name, value.toUpperCase());
									}}
									isInvalid={!!errors.cognome && touched.cognome}
								/>
							</Col>

							<Col md={1}>
								<BftInfoTooltip
									className="mb-3"
									message={
										"Indicare il dato senza caratteri speciali, per le lettere accentate usare l'apostrofo."
									}
								/>
							</Col>
						</Row>
						{(errors.nome || errors.cognome) && (touched.nome || touched.cognome) && (
							<Form.Control.Feedback className="ms-3 mb-3 mt-1" type="invalid">
								{errors.nome ? errors.nome : errors.cognome}
							</Form.Control.Feedback>
						)}

						<BftCodeInput
							length={16}
							id="codiceFiscale"
							key={"codiceFiscale"}
							pattern={"^([A-Za-z0-9]{1,})$"}
							placeholder={
								TipoSoggetto.LR === subjectDetails?.tipoSoggetto
									? "Codice Fiscale*"
									: "Codice Fiscale"
							}
							codeHandler={handleChange("codiceFiscale")}
							inputCode={values.codiceFiscale}
							capitalize={true}
							inputRequired={true}
							isInvalid={!!errors.codiceFiscale && touched.codiceFiscale}
							autofocus
						/>
						{errors.codiceFiscale && touched.codiceFiscale && (
							<Form.Control.Feedback className="ms-3 mb-3 mt-1" type="invalid">
								{errors.codiceFiscale}
							</Form.Control.Feedback>
						)}
						<Row>
							<Col>
								<BftRadioGroup
									controlId="genere"
									label="Genere*"
									options={genereOptions}
									onChange={handleChange}
									isInvalid={!!errors.genere && touched.genere}
									selectedOption={values.genere}
								/>
								{errors.genere && touched.genere && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.genere}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						{subjectDetails?.tipoSoggetto === "RappresentanteLegale" ? (
							<BftBooleanButton
								label="Hai già un conto attivo con noi?"
								name="conto"
								value={FALSE}
								onChange={handleChangeCustom("conto")}
							/>
						) : (
							""
						)}
						{errors.conto && touched.conto && (
							<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
								{errors.conto}
							</Form.Control.Feedback>
						)}
						<Row>
							<Col md={5}>
								<div className="mx-3 mb-3">
									<AppDatePicker
										label="Data di nascita*"
										name="dataDiNascita"
										value={values.dataDiNascita}
										onChange={handleChangeCustom("dataDiNascita", "date")}
										checkMaggioreEta
									/>
									{errors.dataDiNascita && touched.dataDiNascita && (
										<Form.Control.Feedback className="ms-0 mt-1" type="invalid">
											{errors.dataDiNascita}
										</Form.Control.Feedback>
									)}
								</div>
							</Col>
							<Col md={7}>
								<div className="d-flex align-items-center">
									<CountriesDropdown
										options={COUNTRIES}
										value={values.statoDiNascita}
										containerClass="mb-3"
										onChange={handleChangeCustom("statoDiNascita", "nations")}
									/>
								</div>
							</Col>
						</Row>
						<Row>
							<Col md={5}>
								<BftSelect
									name={"siglaProvinciaDiNascita"}
									placeholder={
										values.statoDiNascita?.toUpperCase() === "IT" ? "Provincia*" : "Provincia"
									}
									value={values.siglaProvinciaDiNascita}
									onChange={handleChangeCustom("siglaProvinciaDiNascita", "province")}
									isInvalid={!!errors.siglaProvinciaDiNascita && touched.siglaProvinciaDiNascita}
									disabled={!["IT", "", undefined].includes(values.statoDiNascita?.toUpperCase())}
									options={province}
								/>
								{errors.siglaProvinciaDiNascita && touched.siglaProvinciaDiNascita && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.siglaProvinciaDiNascita}
									</Form.Control.Feedback>
								)}
							</Col>
							<Col md={7}>
								{["IT", "", undefined].includes(values.statoDiNascita?.toUpperCase()) ? (
									<BftSelect
										name={"comuneDiNascita"}
										placeholder={"Comune di nascita*"}
										value={values.comuneDiNascita}
										onChange={handleChange}
										isInvalid={!!errors.comuneDiNascita && touched.comuneDiNascita}
										options={comuni?.[values?.siglaProvinciaDiNascita!]}
										disabled={values?.siglaProvinciaDiNascita === ""}
									/>
								) : (
									<BftInput
										required
										type={"text"}
										name={"comuneDiNascita"}
										placeholder={"Comune di nascita*"}
										value={values.comuneDiNascita}
										onChange={handleChange}
										isInvalid={!!errors.comuneDiNascita && touched.comuneDiNascita}
									/>
								)}
								{errors.comuneDiNascita && touched.comuneDiNascita && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.comuneDiNascita}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						<Row className="justify-content-between">
							<Col md={3}>
								<CountriesDropdown
									options={COUNTRIES_PREFIX}
									containerClass="mb-3"
									value={values.prefissoTel}
									onChange={handleChangeCustom("prefissoTel", "nations")}
								/>
							</Col>
							<Col md={9}>
								<BftInput
									required
									type={"number"}
									name={"numeroTel"}
									placeholder={"Telefono*"}
									value={values.numeroTel}
									onChange={handleChange}
									isInvalid={!!errors.numeroTel && touched.numeroTel}
									onPaste
								/>
								{errors.numeroTel && touched.numeroTel && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.numeroTel}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						<Row>
							<Col>
								<BftInput
									required
									type={"text"}
									name={"email"}
									placeholder={
										TipoSoggetto.LR === subjectDetails?.tipoSoggetto || isInvitation
											? "Indirizzo email*"
											: "Indirizzo email"
									}
									value={values.email}
									onChange={handleChange}
									isInvalid={!!errors.email && touched.email}
								/>
								{errors.email && touched.email && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.email}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						<h5 className="container my-3">Indirizzo di residenza</h5>
						<Row>
							<Col md={5}>
								<BftSelect
									name={"siglaProvinciaResidenza"}
									placeholder={
										values.statoDiNascita?.toUpperCase() === "IT" ? "Provincia*" : "Provincia"
									}
									value={values.siglaProvinciaResidenza}
									onChange={handleChangeCustom("siglaProvinciaResidenza", "province")}
									isInvalid={!!errors.siglaProvinciaResidenza && touched.siglaProvinciaResidenza}
									disabled={
										!["IT", "", undefined].includes(values?.nazioneResidenza?.toUpperCase())
									}
									options={province}
								/>
								{errors.siglaProvinciaResidenza && touched.siglaProvinciaResidenza && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.siglaProvinciaResidenza}
									</Form.Control.Feedback>
								)}
							</Col>
							<Col md={7}>
								{["IT", "", undefined].includes(values?.nazioneResidenza?.toUpperCase()) ? (
									<BftSelect
										name={"comuneResidenza"}
										placeholder={"Comune*"}
										value={values.comuneResidenza}
										onChange={handleChange}
										isInvalid={!!errors.comuneResidenza && touched.comuneResidenza}
										options={comuni?.[values.siglaProvinciaResidenza!]}
										disabled={values?.siglaProvinciaResidenza === ""}
									/>
								) : (
									<BftInput
										required
										type={"text"}
										name={"comuneResidenza"}
										placeholder={"Comune*"}
										value={values.comuneResidenza}
										onChange={handleChange}
										isInvalid={!!errors.comuneResidenza && touched.comuneResidenza}
									/>
								)}
								{errors.comuneResidenza && touched.comuneResidenza && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.comuneResidenza}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						<Row>
							<Col md={5}>
								<BftInput
									required
									type={"text"}
									name={"capResidenza"}
									placeholder={"CAP*"}
									value={values.capResidenza}
									onChange={handleChange}
									isInvalid={!!errors.capResidenza && touched.capResidenza}
								/>
								{errors.capResidenza && touched.capResidenza && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.capResidenza}
									</Form.Control.Feedback>
								)}
							</Col>
							<Col md={7}>
								<div className="d-flex align-items-center">
									<CountriesDropdown
										options={COUNTRIES}
										containerClass="mb-3"
										value={values.nazioneResidenza}
										onChange={handleChangeCustom("nazioneResidenza", "nations")}
									/>
								</div>
							</Col>
						</Row>
						<Row>
							<Col md={9}>
								<BftInput
									required
									type={"text"}
									name={"indirizzoResidenza"}
									placeholder={"Indirizzo di residenza*"}
									value={values.indirizzoResidenza}
									onChange={(e) => {
										const { name, value } = e.target;
										setFieldValue(name, value.toUpperCase());
									}}
									isInvalid={!!errors.indirizzoResidenza && touched.indirizzoResidenza}
								/>
								{errors.indirizzoResidenza && touched.indirizzoResidenza && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.indirizzoResidenza}
									</Form.Control.Feedback>
								)}
							</Col>
							<Col md={3}>
								<BftInput
									required
									type={"text"}
									name={"civicoResidenza"}
									placeholder={"N°*"}
									value={values.civicoResidenza}
									onChange={handleChange}
									isInvalid={!!errors.civicoResidenza && touched.civicoResidenza}
								/>
								{errors.civicoResidenza && touched.civicoResidenza && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors.civicoResidenza}
									</Form.Control.Feedback>
								)}
							</Col>
						</Row>
						<div className="mx-2 my-4">
							<BftButton className="btn-buffetti col-12" type="submit">
								Avanti
							</BftButton>
						</div>
					</form>
				);
			}}
		</Formik>
	);
};

export default Anagrafica;
